<template>
  <div class="product-withdraw">
    <FadeTransition>
      <NavBar v-if="showNavbar" sticky>
        <template #left>
          <BackButton @click="onBack" />
        </template>
        <template #right>
          <BackButton icon="EF0030" @click="onClose" />
        </template>
      </NavBar>
    </FadeTransition>

    <LoaderIndicator
      v-if="isLoading"
      class="product-withdraw__loader"
      size="32"
      with-container
    />

    <HelperViewStatus
      v-else-if="hasError"
      class="product-withdraw__helper"
      generic-error
      button-label="Tentar novamente"
      @click="fetchWithdrawDetails"
    />

    <FadeTransition v-else>
      <component :is="step.component" />
    </FadeTransition>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import {
  NavBar,
  LoaderIndicator,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { FadeTransition } from '@/modules/common/components/FadeTransition';

import {
  getWithdrawDetails,
  getWithdrawReview,
  makeWithdraw,
  IWithdrawDetails,
  IWithdrawReview,
  IWithdrawConclusion,
  IWithdrawDetailsV2,
  getWithdrawDetailsV2,
  makeProductWithdraw,
  getProductWithdrawReview
} from '@/modules/transaction/services/product-withdraw';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { ProductWithdrawComponents, EProductWithdrawSteps } from './helpers';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

const route = useRoute();
const router = useRouter();

const isOnGoingRedemptionsActive = computed(() => {
  return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_ON_GOING_REDEMPTIONS);
});

const currentStep = ref(
  isOnGoingRedemptionsActive.value
    ? EProductWithdrawSteps.WITHDRAWV2
    : EProductWithdrawSteps.WITHDRAW
);
const withdrawDetails = ref<IWithdrawDetails>();
const withdrawDetailsV2 = ref<IWithdrawDetailsV2>();
const withdrawReview = ref<IWithdrawReview>();
const withdrawConclusion = ref<IWithdrawConclusion>();

const isLoading = ref(true);
const isFetchingReview = ref(false);
const isFetchingWithdraw = ref(false);
const hasError = ref(false);
const amount = ref(0);

const step = computed(() => {
  return {
    component: ProductWithdrawComponents[currentStep.value]
  };
});

const showNavbar = computed(() => {
  return currentStep.value !== EProductWithdrawSteps.CONCLUSION;
});

const productId = computed(() => {
  const { id } = route.params;

  return id;
});

const categoryId = computed(() => {
  const { type } = route.params;

  return type;
});

const isEnabledProductBffUsaged = computed(() => {
  return FeatureFlagsModule.isEnabled(
    FeatureFlags.ENABLE_BFF_INSTRUMENTS_MIGRATION
  );
});

function onBack() {
  if (
    currentStep.value === EProductWithdrawSteps.WITHDRAW ||
    currentStep.value === EProductWithdrawSteps.WITHDRAWV2
  )
    return onClose();

  currentStep.value = currentStep.value - 1;
}

function onClose() {
  if (window.history.length > 1) {
    window.history.back();
  } else {
    router.push({
      name: 'productsCatalog',
      params: {
        type: route.params.type || 'fixed-income'
      }
    });
  }
}

async function fetchWithdrawDetails() {
  try {
    isLoading.value = true;
    hasError.value = false;

    withdrawDetails.value = await getWithdrawDetails(productId.value);
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchWithdrawDetailsV2() {
  try {
    isLoading.value = true;
    hasError.value = false;

    withdrawDetailsV2.value = await getWithdrawDetailsV2(
      productId.value,
      categoryId.value
    );
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchWithdrawReview(amountValue: number) {
  amount.value = amountValue;

  try {
    isFetchingReview.value = true;

    withdrawReview.value = isEnabledProductBffUsaged.value
      ? await getProductWithdrawReview(
        productId.value,
        amount.value
      ) : await getWithdrawReview(
        productId.value,
        amount.value
      );

    currentStep.value = EProductWithdrawSteps.REVIEW;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  } finally {
    isFetchingReview.value = false;
  }
}

async function fetchWithdraw() {
  try {
    isFetchingWithdraw.value = true;

    withdrawConclusion.value = isEnabledProductBffUsaged.value
      ? await makeProductWithdraw(
        productId.value,
        amount.value
      ) : await makeWithdraw(
        productId.value,
        amount.value
      );

    currentStep.value = EProductWithdrawSteps.CONCLUSION;
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });
  } finally {
    isFetchingWithdraw.value = false;
  }
}

onMounted(() =>
  isOnGoingRedemptionsActive.value
    ? fetchWithdrawDetailsV2()
    : fetchWithdrawDetails()
);

provide('withdrawDetails', withdrawDetails);
provide('withdrawDetailsV2', withdrawDetailsV2);
provide('withdrawReview', withdrawReview);
provide('withdrawConclusion', withdrawConclusion);
provide('isFetchingReview', isFetchingReview);
provide('isFetchingWithdraw', isFetchingWithdraw);
provide('productId', productId);

provide('fetchWithdrawReview', fetchWithdrawReview);
provide('fetchWithdraw', fetchWithdraw);
</script>

<style lang="less" scoped>
@height: calc(100vh - 64px);

.product-withdraw {
  .navbar-container {
    border-bottom: none;
  }

  &__loader {
    height: @height;
  }

  &__helper {
    margin: 0 auto;
    height: @height;
    justify-content: center;
  }
}
</style>
